<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        sm="6"
        v-for="(address, i) of addresses"
        :key="`address_${i}`"
        class="d-flex flex-column"
      >
        <Address
          :address="address"
          v-if="address._id"
        >
          <v-btn
            text
            color="primary"
            :disabled="readonly"
            @click="editAddress(i)"
          >
            <v-icon>edit</v-icon>
            Edit
          </v-btn>
          <v-spacer />
          <v-btn
            icon
            @click="removeAddress(i)"
            :disabled="readonly || addresses.length <= 1"
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </Address>
      </v-col>
    </v-row>

    <v-row v-show="!!alerts.length">
      <v-col>
        <v-alert
          v-for="alert of alerts"
          :key="alert.text"
          :type="alert.type"
          dense
          text
          border="left"
        >
          <v-row align="center">
            <v-col class="grow">
              {{ alert.text }}
            </v-col>
            <v-col class="shrink">
              <v-btn
                outlined
                color="primary"
                @click="addAddress(alert.value)"
              >
                <v-icon>
                  add
                </v-icon>
                Add
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-btn
        depressed
        tile
        class="ma-0"
        :disabled="readonly"
        @click="addAddress"
      >
        <v-icon>
          add
        </v-icon>
        Add address
      </v-btn>
    </v-row>

    <v-dialog
      v-model="addressDialog"
      width="500"
    >
      <v-card>
        <v-card-text>
          <v-row class="pt-5">
            <address-form
              v-model="addressForm"
              @valid="valid = $event"
            />
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="closeAddressDialog"
            :disabled="!valid"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="addressDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-dialog
      :value="confirmDialog"
      @input="confirmRemoveAddress"
    >
      Are you sure you want to remove this address? This action cannot be reversed.
    </confirm-dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { pick, cloneDeep } from 'lodash'
import { addressTypeMap } from '../utilities/index.js'
import Address from './Address.vue'
import AddressForm from './forms/AddressForm.vue'
import ConfirmDialog from './dialogs/ConfirmDialog.vue'

export default {
  name: 'RegistrationAddresses',

  components: {
    Address,
    AddressForm,
    ConfirmDialog
  },

  props: {
    registrationId: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    requiredAddressTypes: {
      type: Array,
      default: () => ([])
    }
  },

  data: () => ({
    valid: false,
    addresses: [],
    addressDialog: false,
    editedAddress: -1,
    addressForm: null,
    confirmDialog: false,
    confirmValue: null,
    registration: {
      addresses: []
    }
  }),

  computed: {
    alerts () {
      const alerts = []

      const addressTypes = this.registration.addresses.map(o => o.type)
      for (const requiredAddressType of this.requiredAddressTypes) {
        if (!addressTypes.includes(requiredAddressType)) {
          const name = addressTypeMap[requiredAddressType] ?? requiredAddressType
          alerts.push({
            type: 'error',
            text: `Please add a ${name}.`,
            value: requiredAddressType
          })
        }
      }

      return alerts
    },

    _valid () {
      return this.alerts.length < 1
    }
  },

  apollo: {
    registration: {
      query: gql`query GetRegistration($id: MongoID!) {
        registration: registrationById(_id: $id) {
          _id
          addresses {
            _id
            type
            addressLines
            localityName
            postcode
            state
            country
          }
        }
      }`,
      variables () {
        return {
          id: this.registrationId
        }
      },
      result ({ data }) {
        if (!data) return
        const { registration } = data
        this.addresses = registration.addresses ?? []
        this.$emit('valid', this._valid)
      }
    }
  },

  methods: {
    addAddress (type = null) {
      this.editedAddress = -1
      this.addressForm = {
        type,
        addressLines: [null],
        localityName: null,
        postcode: null,
        state: null,
        country: 'Australia'
      }
      this.addressDialog = true
    },

    editAddress (i) {
      this.addressForm = cloneDeep(this.addresses[i])
      this.editedAddress = i
      this.addressDialog = true
    },

    removeAddress (i) {
      this.confirmValue = i
      this.confirmDialog = true
    },

    async confirmRemoveAddress (value) {
      if (value) {
        this.addresses.splice(this.confirmValue, 1)
        await this.saveAddresses()
        this.$emit('info', 'Address removed.')
      }
      this.confirmDialog = false
      this.confirmValue = null
    },

    async closeAddressDialog () {
      this.addressForm.addressLines = this.addressForm.addressLines.filter(v => v && v.trim())
      if (this.editedAddress >= 0) {
        Object.assign(this.addresses[this.editedAddress], this.addressForm)
      } else {
        this.addresses.push(this.addressForm)
      }
      this.addressDialog = false
      await this.saveAddresses()
      this.$emit('info', 'Address saved.')
    },

    async saveAddresses () {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdRegistrationInput!) {
            registrationUpdateById(_id: $id, record: $record){
              record {
                _id
              }
            }
          }`,
          variables: {
            id: this.registrationId,
            record: {
              addresses: this.addresses.map(address => pick(address, ['type', 'addressLines', 'localityName', 'postcode', 'state', 'country']))
            }
          }
        })
        this.$apollo.queries.registration.refresh()
        this.$emit('change')
      } catch (e) {
        this.$emit('error', e)
      }
    }
  }
}
</script>
